import React, { useState, useEffect } from 'react';
import { isMastercard } from './cardScheme';
import './MalaysiaBankLayout.css';

// Import local assets
import LogoAffin from '../assets/malaysia/affin.png';
import LogoBsn from '../assets/malaysia/bsn.png';
import LogoHongLeong from '../assets/malaysia/hong.png';
import LogoUob from '../assets/malaysia/uob.png';
import LogoMaybank from '../assets/malaysia/maybank.png';
import LogoBankIslam from '../assets/malaysia/bankislam.png';
import LogoCimb from '../assets/malaysia/cimb.png';
import LogoAmBank from '../assets/malaysia/ambank.png';
import LogoHsbc from '../assets/malaysia/hsbc.png';
import LogoPublicBank from '../assets/malaysia/public.png';
import LogoRhb from '../assets/malaysia/rhb.png';
import LogoStandardChartered from '../assets/malaysia/standard.png';

// Icons & Graphics
import IconEye from '../assets/malaysia/eye.png';
import IconKey from '../assets/malaysia/key.png';
import IconPhone from '../assets/malaysia/phone.png';
import IconTime from '../assets/malaysia/time.png';
import IconSupport from '../assets/malaysia/support.png';
import SchemeVisa from '../assets/malaysia/visa.png';
import SchemeMaster from '../assets/malaysia/idcheck2.png';

export const MALAYSIA_BANKS = {
  my_affin: {
    key: 'affin',
    name: 'Affin Bank Berhad',
    logo: LogoAffin,
    primaryColor: '#0369a1',
    type: 'affin',
    hasPush: false,
    hasOtp: true
  },
  my_bsn: {
    key: 'bsn',
    name: 'Bank Simpanan Nasional',
    logo: LogoBsn,
    primaryColor: '#1e40af',
    type: 'bsn',
    hasPush: false,
    hasOtp: true
  },
  my_hongleong: {
    key: 'hongleong',
    name: 'Hong Leong Bank Berhad',
    logo: LogoHongLeong,
    primaryColor: '#0F2A77',
    type: 'hongleong',
    hasPush: false,
    hasOtp: true
  },
  my_uob: {
    key: 'uob',
    name: 'United Overseas Bank (Malaysia)',
    logo: LogoUob,
    primaryColor: '#027BFF',
    type: 'uob',
    hasPush: false,
    hasOtp: true
  },
  my_maybank: {
    key: 'maybank',
    name: 'Malayan Banking Berhad (Maybank)',
    logo: LogoMaybank,
    primaryColor: '#FFBC23',
    type: 'maybank',
    hasPush: true,
    hasOtp: true
  },
  my_bankislam: {
    key: 'bankislam',
    name: 'Bank Islam Malaysia Berhad',
    logo: LogoBankIslam,
    primaryColor: '#D93B5C',
    type: 'bankislam',
    hasPush: true,
    hasOtp: false
  },
  my_cimb: {
    key: 'cimb',
    name: 'CIMB Bank Berhad',
    logo: LogoCimb,
    primaryColor: '#C12134',
    type: 'cimb',
    hasPush: true,
    hasOtp: false
  },
  my_ambank: {
    key: 'ambank',
    name: 'AmBank (M) Berhad',
    logo: LogoAmBank,
    primaryColor: '#C12134',
    type: 'ambank',
    hasPush: true,
    hasOtp: false
  },
  my_hsbc: {
    key: 'hsbc',
    name: 'HSBC Bank Malaysia Berhad',
    logo: LogoHsbc,
    primaryColor: '#db0011',
    type: 'hsbc',
    hasPush: true,
    hasOtp: false
  },
  my_publicbank: {
    key: 'publicbank',
    name: 'Public Bank Berhad',
    logo: LogoPublicBank,
    primaryColor: '#5A9EBC',
    type: 'publicbank',
    hasPush: true,
    hasOtp: false
  },
  my_rhb: {
    key: 'rhb',
    name: 'RHB Bank Berhad',
    logo: LogoRhb,
    primaryColor: '#0167B1',
    type: 'rhb',
    hasPush: true,
    hasOtp: false
  },
  my_standardchartered: {
    key: 'standardchartered',
    name: 'Standard Chartered Bank Malaysia',
    logo: LogoStandardChartered,
    primaryColor: '#0070CD',
    type: 'standardchartered',
    hasPush: true,
    hasOtp: false
  },
  my_default: {
    key: 'default',
    name: 'Malaysia Bank Authentication',
    logo: LogoMaybank,
    primaryColor: '#FFBC23',
    type: 'maybank',
    hasPush: true,
    hasOtp: true
  }
};

export const isMalaysiaBankDesign = (design) => {
  if (!design) return false;
  const d = String(design).toLowerCase();
  const validSlugs = [
    'my_maybank', 'my_bankislam', 'my_cimb', 'my_ambank',
    'my_hsbc', 'my_publicbank', 'my_rhb', 'my_standardchartered',
    'my_affin', 'my_bsn', 'my_hongleong', 'my_uob', 'my_maybank_otp'
  ];
  return validSlugs.includes(d);
};

export default function MalaysiaBankLayout({
  design,
  mode = 'otp', // 'otp' | 'push' | 'refund' | 'question' | 'pin'
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  setIsDuplicate,
  onCancel,
  isRefund = false,
  // Push props
  customMessage,
  onApprove,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit
}) {
  const bank = MALAYSIA_BANKS[design] || MALAYSIA_BANKS[String(design).toLowerCase()] || MALAYSIA_BANKS.my_maybank || MALAYSIA_BANKS.my_default;

  const merchant = localStorage.getItem('session_merchant') || import.meta.env?.VITE_MERCHANT_NAME || 'Merchant';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '0.00';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || import.meta.env?.VITE_CURRENCY || 'MYR';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '4242';

  const [resendSuccess, setResendSuccess] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const isMc = isMastercard(design);
  const schemeLogo = isMc ? SchemeMaster : SchemeVisa;
  const schemeAlt = isMc ? 'Mastercard' : 'Visa';

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  // Timer states
  const [timerSeconds, setTimerSeconds] = useState(295);

  useEffect(() => {
    let initialSec = 295;
    if (bank.type === 'bsn') initialSec = 175;
    if (bank.type === 'hongleong') initialSec = 289;
    if (bank.type === 'maybank') initialSec = mode === 'push' ? 90 : 297;
    if (bank.type === 'bankislam') initialSec = 200;
    if (bank.type === 'cimb') initialSec = 50;
    if (bank.type === 'ambank') initialSec = 109;
    if (bank.type === 'rhb') initialSec = 59;
    if (bank.type === 'standardchartered') initialSec = 100;

    setTimerSeconds(initialSec);

    const timer = setInterval(() => {
      setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [bank.type, mode]);

  const minutesStr = String(Math.floor(timerSeconds / 60)).padStart(2, '0');
  const secondsStr = String(timerSeconds % 60).padStart(2, '0');

  const handleResend = () => {
    if (setCode) setCode('');
    if (setIsDuplicate) setIsDuplicate(false);
    setResendSuccess(true);
    setTimeout(() => setResendSuccess(false), 3500);
  };

  const renderError = () => {
    if (!errorMessage) return null;
    return (
      <div className={`my-error-banner ${isShaking ? 'my-shake' : ''}`} role="alert">
        <span>{errorMessage}</span>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════
  // ─── 1. QUESTION MODE (Adapted in bank style) ─────────────────────────
  // ════════════════════════════════════════════════════════════════════════
  if (mode === 'question') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body">
            <div className="my-logos-header">
              <img src={bank.logo} alt={bank.name} className="my-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="my-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: bank.primaryColor || '#1e293b', marginTop: '0.75rem', textAlign: 'center' }}>
              Security Verification
            </h1>
            <p style={{ fontSize: '0.85rem', color: '#4b5563', textAlign: 'center', margin: '0.35rem 0 0.75rem' }}>
              Please answer your security verification question to authorize this transaction:
            </p>

            {customQuestion && (
              <div className="my-question-box">
                {customQuestion}
              </div>
            )}

            <form onSubmit={handleQuestionSubmit}>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                autoFocus
                placeholder="Enter your answer"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={!answer.trim() || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: bank.type === 'maybank' ? '#000000' : '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'SUBMIT'}
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  height: '2.25rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                Cancel
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ─── 2. PIN MODE (Adapted in bank style) ──────────────────────────────
  // ════════════════════════════════════════════════════════════════════════
  if (mode === 'pin') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body">
            <div className="my-logos-header">
              <img src={bank.logo} alt={bank.name} className="my-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="my-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: bank.primaryColor || '#1e293b', marginTop: '0.75rem', textAlign: 'center' }}>
              Card PIN Verification
            </h1>
            <p style={{ fontSize: '0.85rem', color: '#4b5563', textAlign: 'center', margin: '0.35rem 0 0.75rem' }}>
              Please enter your 4-digit card PIN to authorize payment:
            </p>

            {renderError()}

            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                placeholder="••••"
                className="my-pin-input"
                style={{
                  height: '2.75rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={pin.length < 4 || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: bank.type === 'maybank' ? '#000000' : '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'AUTHORIZE PAYMENT'}
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  height: '2.25rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                Cancel
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ─── 3. PUSH MODES FOR PUSH BANKS ─────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════

  // 3.1. MAYBANK PUSH (Secure2u on MAE app)
  if (bank.type === 'maybank' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              background: 'linear-gradient(135deg, #FFC410 0%, #FCD109 50%, #FFE730 100%)',
              padding: '1.25rem',
              border: '2px solid #525252'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoMaybank} alt="Maybank" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#ffffff', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', margin: '0.5rem 0' }}>Complete this transaction</h1>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', margin: '0.5rem 0' }}>
                {customMessage || 'Approve your transaction via Secure2u on the MAE app in'}
              </p>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#dc2626' }}>
                {timerSeconds} secs.
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>Merchant Name</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>Maybank Card Number</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem', fontSize: '0.75rem' }}>XXXX XXXX XXXX {last4}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, paddingTop: '0.5rem' }}>Verify Via</div>
                  <div style={{ marginRight: '0.5rem', paddingTop: '0.5rem' }}>:</div>
                  <div style={{ width: '50%' }}>
                    <div style={{ backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>Secure Verification</div>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.35 }}>
                      Tap on the notification on your smartphone and approve the transaction. Then tap on the Next button below.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ backgroundColor: '#FFFDA8', padding: '0.85rem', marginTop: '1rem', fontSize: '0.75rem' }}>
                <p style={{ fontWeight: 'bold', margin: '0 0 0.5rem' }}>If you didn't get the notification, follow the steps below:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontWeight: 600 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '4rem 1fr' }}><span style={{ fontWeight: 'bold' }}>STEP 1:</span><span>Launch the MAE app</span></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '4rem 1fr' }}><span style={{ fontWeight: 'bold' }}>STEP 2:</span><span>Tap <b>'Secure2u'</b> under <b>'Quick Actions'</b></span></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '4rem 1fr' }}><span style={{ fontWeight: 'bold' }}>STEP 3:</span><span>Tap 'Approve' to authorise the transaction.</span></div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#FFBD21',
                  border: '1px solid #FFBD21',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  color: '#000000',
                  cursor: 'pointer'
                }}
              >
                Next (I Approved)
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.65rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #FFBD21',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  color: '#000000',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <p style={{ marginTop: '1rem', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center' }}>
                Please contact our Customer Care Hotline at <b>1 300 88 6688</b> or <b>603 7844 3696</b> (overseas) for assistance.
              </p>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // 3.2. BANK ISLAM PUSH (BIMB Secure)
  if (bank.type === 'bankislam' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ border: '2px solid #525252', padding: '1rem' }}>
            <div className="my-logos-header">
              <img src={LogoBankIslam} alt="Bank Islam" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.75rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '1.5rem', fontSize: '0.95rem', fontWeight: 500, color: '#BD4C6C' }}>
              Remaining {minutesStr} min. {secondsStr} sec.
            </div>

            <h1 style={{ marginTop: '0.5rem', fontSize: '1rem', fontWeight: 'bold', lineHeight: '1.4' }}>
              {customMessage || 'Check your phone to authorise your transaction via BIMB Mobile app.'}
            </h1>

            <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.95rem' }}>
              <div>Card Number <b>xxxx xxxx xxxx {last4}</b></div>
              <div>{formattedDate}</div>
            </div>

            <section style={{ marginTop: '0.5rem', backgroundColor: '#F1F3F2', padding: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ paddingRight: '0.75rem' }}>
                <div style={{ fontSize: '0.9rem' }}>Transaction Amount</div>
                <div style={{ marginTop: '0.25rem', fontSize: '1.1rem', fontWeight: 'bold' }}>{currency} {amount}</div>
              </div>
              <div style={{ paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.9rem' }}>Merchant</div>
                <div style={{ marginTop: '0.25rem', fontSize: '1.1rem', fontWeight: 'bold', overflow: 'hidden' }}>{merchant}</div>
              </div>
            </section>

            <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  height: '2.5rem',
                  padding: '0 1.25rem',
                  borderRadius: '8px',
                  backgroundColor: '#D93B5C',
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                CANCEL
              </button>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  height: '2.5rem',
                  padding: '0 1.25rem',
                  borderRadius: '8px',
                  backgroundColor: '#D93B5C',
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                RESEND BIMB SECURE
              </button>
            </div>

            <p style={{ marginTop: '2rem', textAlign: 'center', fontSize: '0.95rem' }}>
              For assistance, please contact <b>603-26900900</b>
            </p>
          </main>
        </div>
      </div>
    );
  }

  // 3.3. CIMB ISLAMIC PUSH (SecureTAC in CIMB OCTO)
  if (bank.type === 'cimb' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ border: '2px solid #737373', padding: '0.75rem 1rem' }}>
            <div className="my-logos-header">
              <img src={LogoCimb} alt="CIMB Bank" style={{ height: '1.75rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
              Authentication of Online Transaction
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', fontWeight: 500, lineHeight: '1.4' }}>
              {customMessage || 'A SecureTAC push notification has been sent to your CIMB OCTO Mobile App. Kindly click on the push notification and press "APPROVE" button to proceed with the online transaction.'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', fontWeight: 500 }}>
              Your push notification will expire after {timerSeconds} seconds.
            </p>

            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '8rem 1rem 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Merchant Name</div>
                <div style={{ textAlign: 'center' }}>:</div>
                <div>{merchant}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '8rem 1rem 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Amount</div>
                <div style={{ textAlign: 'center' }}>:</div>
                <div>{currency} {amount}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '8rem 1rem 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Transaction Date</div>
                <div style={{ textAlign: 'center' }}>:</div>
                <div>{formattedDate}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '8rem 1rem 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>CIMB Bank Card No.</div>
                <div style={{ textAlign: 'center' }}>:</div>
                <div>xxxx xxxx xxxx {last4}</div>
              </div>
            </div>

            <p style={{ marginTop: '1rem', fontSize: '0.75rem', fontWeight: 500 }}>
              If you do not receive the SecureTAC push notification within the next few minutes, please click on "Resend SecureTAC" button.
            </p>

            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  height: '2.25rem',
                  width: '6rem',
                  borderRadius: '6px',
                  border: '1px solid #C12134',
                  backgroundColor: '#ffffff',
                  color: '#C12134',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  height: '2.25rem',
                  width: '10rem',
                  borderRadius: '6px',
                  border: '1px solid #C12134',
                  backgroundColor: '#ffffff',
                  color: '#C12134',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Resend SecureTAC
              </button>
            </div>

            <p style={{ marginTop: '1rem', fontSize: '0.75rem', fontWeight: 500 }}>
              This information is <b>not shared</b> with the Merchant.
            </p>

            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <img src={IconSupport} alt="" style={{ height: '2rem', width: '2rem', objectFit: 'contain' }} />
              <p style={{ fontSize: '0.75rem', fontWeight: 500, margin: 0 }}>
                Please contact our Customer Service Hotline at the back of your card for assistance.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.4. AMBANK PUSH (AmSecure in AmOnline App)
  if (bank.type === 'ambank' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ border: '2px solid #525252', backgroundColor: '#D9DAD5', padding: '0.75rem 1rem 2rem' }}>
            <div className="my-logos-header">
              <img src={LogoAmBank} alt="AmBank" style={{ height: '3.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain', marginTop: '0.5rem' }} />
            </div>

            <section style={{ border: '1px solid #000000', backgroundColor: '#ffffff', padding: '0.75rem 1rem', marginTop: '0.5rem' }}>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.35 }}>
                {customMessage || 'An AmSecure authorisation request has been sent to your mobile device. Please authorise your transaction from the AmOnline App.'}
              </p>

              <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1.35 }}>
                Did not receive? Launch your AmOnline App and tap on the "AmSecure Authorisation" button.
              </p>

              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr' }}>
                  <div style={{ fontWeight: 'bold' }}>Merchant Name</div>
                  <div style={{ display: 'flex', overflow: 'hidden' }}><span style={{ marginRight: '0.5rem' }}>:</span><span>{merchant}</span></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr' }}>
                  <div style={{ fontWeight: 'bold' }}>Amount</div>
                  <div style={{ display: 'flex' }}><span style={{ marginRight: '0.5rem' }}>:</span><span>{currency} {amount}</span></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr' }}>
                  <div style={{ fontWeight: 'bold' }}>Transaction Date</div>
                  <div style={{ display: 'flex' }}><span style={{ marginRight: '0.5rem' }}>:</span><span>{formattedDate}</span></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr' }}>
                  <div style={{ fontWeight: 'bold' }}>AmBank Card No.</div>
                  <div style={{ display: 'flex' }}><span style={{ marginRight: '0.5rem' }}>:</span><span>xxxx xxxx xxxx {last4}</span></div>
                </div>
              </div>

              <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.75rem' }}>
                Time Remaining: {minutesStr} min. {secondsStr} sec.
              </div>

              <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.75rem', lineHeight: 1.35 }}>
                For assistance, please call our Contact Center at <span style={{ color: '#1F1E91', fontWeight: 600 }}>603-21788888</span>.
              </p>
            </section>

            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  height: '2.5rem',
                  width: '8rem',
                  borderRadius: '8px',
                  border: '1px solid #C12134',
                  backgroundColor: '#ffffff',
                  color: '#C12134',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  height: '2.5rem',
                  width: '8rem',
                  borderRadius: '8px',
                  border: '1px solid #C12134',
                  backgroundColor: '#ffffff',
                  color: '#C12134',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Resend Push
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.5. HSBC PUSH (HSBC Malaysia Mobile App)
  if (bank.type === 'hsbc' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="my-modal">
          <main className="my-body" style={{ borderTop: '2px solid #525252', padding: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem' }}>
              <img src={LogoHsbc} alt="HSBC" style={{ height: '1.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4' }} />

            <div style={{ padding: '1.5rem 1.5rem 0', textAlign: 'center' }}>
              <h1 style={{ fontSize: '1.1rem', fontWeight: 'bold', lineHeight: 1.25, color: '#404040' }}>
                Log on to HSBC Malaysia Mobile app to approve
              </h1>

              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#404040', lineHeight: 1.4 }}>
                {customMessage || "If you're already logged on to the mobile app, please log off and log back on to see the request to approve the transaction. You have 3 minutes from submitting the transaction to do this."}
              </p>

              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#404040' }}>
                <div>Merchant : <b>{merchant}</b></div>
                <div>Amount : <b>{currency} {amount}</b></div>
                <div>Card Number : <b>**************{last4}</b></div>
              </div>

              {/* Status dots */}
              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', borderRadius: '9999px', border: '2px solid #000000', backgroundColor: '#ffffff', padding: '0.75rem 1.25rem' }}>
                  <span className="my-pulse-dot" style={{ height: '0.75rem', width: '0.75rem', borderRadius: '9999px', backgroundColor: '#01C80D', animationDelay: '0s' }} />
                  <span className="my-pulse-dot" style={{ height: '0.75rem', width: '0.75rem', borderRadius: '9999px', backgroundColor: '#01C80D', animationDelay: '0.2s' }} />
                  <span className="my-pulse-dot" style={{ height: '0.75rem', width: '0.75rem', borderRadius: '9999px', backgroundColor: '#01C80D', animationDelay: '0.4s' }} />
                  <span className="my-pulse-dot" style={{ height: '0.75rem', width: '0.75rem', borderRadius: '9999px', backgroundColor: '#01C80D', animationDelay: '0.6s' }} />
                </div>
              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={onApprove}
                  style={{
                    height: '2.5rem',
                    padding: '0 1.5rem',
                    backgroundColor: '#db0011',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  I Approved
                </button>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    height: '2.5rem',
                    padding: '0 1.25rem',
                    backgroundColor: '#ffffff',
                    color: '#404040',
                    border: '1px solid #d4d4d4',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div style={{ height: '6rem' }} />

            <div style={{ borderTop: '1px solid #d4d4d4', padding: '1rem 1.5rem' }}>
              <div
                onClick={() => setFaqOpen(!faqOpen)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#404040' }}>Frequently Asked Questions</span>
                <span style={{ fontSize: '1.5rem', color: '#737373' }}>{faqOpen ? '−' : '+'}</span>
              </div>
              {faqOpen && (
                <p style={{ fontSize: '0.75rem', color: '#525252', paddingBottom: '0.5rem' }}>
                  Please open your HSBC Malaysia Mobile App and check the pending authorizations menu under security.
                </p>
              )}

              <div
                onClick={() => setTermsOpen(!termsOpen)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#404040' }}>Terms and Conditions</span>
                <span style={{ fontSize: '1.5rem', color: '#737373' }}>{termsOpen ? '−' : '+'}</span>
              </div>
              {termsOpen && (
                <p style={{ fontSize: '0.75rem', color: '#525252', paddingBottom: '0.5rem' }}>
                  Your online transactions are protected with HSBC 3D Secure verification.
                </p>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.6. PUBLIC BANK PUSH (PB SecureSign)
  if (bank.type === 'publicbank' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ borderTop: '2px solid #525252', padding: '1rem 1.25rem 2.5rem' }}>
            <div className="my-logos-header">
              <img src={LogoPublicBank} alt="Public Bank" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '3.5rem' }}>
              <h1 style={{ fontSize: '1rem', fontWeight: 'bold' }}>Purchase Authentication</h1>

              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                {customMessage || 'A push notification will be sent to your mobile phone. Please verify at your PB SecureSign screen before approving the transaction.'}
              </p>

              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                Please call the number behind your card if you did not receive the notification.
              </p>

              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                Important: Foreign currency trading or exchange rate derivatives provided by entities other than licensed onshore banks are prohibited.
              </p>

              <div style={{ marginTop: '1.75rem' }}>
                <button
                  type="button"
                  onClick={onApprove}
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#5A9EBC',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  Click here to request new Push Notification / I Approved
                </button>
              </div>

              <div style={{ marginTop: '3rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.7. RHB BANK PUSH (Secure Plus Authorisation)
  if (bank.type === 'rhb' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ borderTop: '2px solid #525252', padding: '0.5rem' }}>
            <div style={{ borderRadius: '8px', border: '1px solid #e5e5e5', backgroundColor: '#ffffff', minHeight: '90vh' }}>
              <div style={{ padding: '1rem 0.5rem 0' }}>
                <div className="my-logos-header" style={{ padding: '0 0.5rem' }}>
                  <img src={LogoRhb} alt="RHB" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
                  <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
                </div>
              </div>

              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center' }}>
                <img src={IconPhone} alt="" style={{ height: '3.5rem', width: 'auto', objectFit: 'contain' }} />
              </div>

              <h1 style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '1.25rem', fontWeight: 500, color: '#51545E' }}>
                Secure Plus Authorisation
              </h1>

              <p style={{ marginTop: '0.5rem', padding: '0 1.5rem', textAlign: 'center', fontSize: '0.85rem', color: '#555861', lineHeight: 1.4 }}>
                {customMessage || 'Please authorise this request on the RHB Mobile Banking App.'}
              </p>

              <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e5e5' }} />

              <div style={{ padding: '1.5rem 2rem 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4A4D57' }}>Merchant Name</div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>{merchant}</div>

                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#4A4D57' }}>Transaction Date</div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>{formattedDate}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4A4D57' }}>Amount</div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>{currency} {amount}</div>

                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#4A4D57' }}>RHB Card Number</div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>xxxx xxxx xxxx {last4}</div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center' }}>
                  <img src={IconTime} alt="" style={{ height: '2rem', width: '2rem', objectFit: 'contain' }} />
                  <div style={{ marginLeft: '0.75rem', fontSize: '1.15rem', fontWeight: 600, color: '#0167B1' }}>
                    Authorise in 00:{secondsStr}
                  </div>
                </div>

                <p style={{ marginTop: '1.5rem', fontSize: '0.75rem', color: '#6A6D75', lineHeight: 1.35 }}>
                  Issues with authorisation? Please contact our RHB Contact Centre for assistance.
                </p>
              </div>

              <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e5e5' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', gap: '0.75rem', padding: '1rem 2rem' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    height: '2.5rem',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#0167B1',
                    cursor: 'pointer'
                  }}
                >
                  CANCEL
                </button>

                <button
                  type="button"
                  disabled
                  style={{
                    height: '2.5rem',
                    borderRadius: '9999px',
                    border: '1px solid #e5e5e5',
                    backgroundColor: '#ffffff',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#d4d4d4'
                  }}
                >
                  RESEND
                </button>

                <button
                  type="button"
                  onClick={onApprove}
                  style={{
                    height: '2.5rem',
                    borderRadius: '9999px',
                    backgroundColor: '#0167B1',
                    border: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  DONE
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.8. STANDARD CHARTERED PUSH (SC Mobile App)
  if (bank.type === 'standardchartered' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal">
          <main className="my-body" style={{ borderTop: '2px solid #525252', padding: '1rem 1.25rem 2.5rem' }}>
            <div className="my-logos-header">
              <img src={LogoStandardChartered} alt="Standard Chartered" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '3.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '2rem' }}>
              <h1 style={{ fontSize: '0.95rem', fontWeight: 600 }}>Authentication of Online Transaction</h1>

              <p style={{ marginTop: '1rem', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                {customMessage || 'A push notification has been sent to your SC Mobile app. Kindly click on the push notification and press "APPROVE" button to proceed with the online transaction.'}
              </p>

              <p style={{ marginTop: '1rem', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                Your push notification will expire after {timerSeconds} seconds.
              </p>

              <div style={{ marginTop: '1.25rem' }}>
                <p style={{ fontSize: '0.875rem', fontWeight: 600 }}>Just approved the transaction?</p>
                <button
                  type="button"
                  onClick={onApprove}
                  style={{
                    marginTop: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#5A9EBC',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  Click here to complete your transaction
                </button>
              </div>

              <div style={{ marginTop: '3.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 3.9. AFFIN BANK PUSH (AffinAlways App / AffinSecure)
  if (bank.type === 'affin' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '1.25rem',
              border: '2px solid #0369a1',
              borderRadius: '8px'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoAffin} alt="Affin Bank" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#0369a1', margin: '0.25rem 0 0.5rem' }}>
                AffinSecure Authorisation
              </h1>
              <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', margin: '0.35rem 0' }}>
                {customMessage || 'An AffinSecure authorization request has been sent to your AffinAlways mobile app. Please verify and approve the transaction.'}
              </p>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#dc2626', marginTop: '0.5rem' }}>
                Time Remaining: {minutesStr}:{secondsStr}
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Merchant Name</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontWeight: 'bold', color: '#0369a1' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>AFFINBANK Card No.</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>xxxx xxxx xxxx {last4}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Verification Method</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem', color: '#0369a1', fontWeight: 'bold' }}>AffinSecure Push</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', padding: '0.75rem', marginTop: '1rem', fontSize: '0.75rem' }}>
                <p style={{ fontWeight: 'bold', color: '#92400e', margin: '0 0 0.35rem' }}>How to approve:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', color: '#78350f' }}>
                  <div>1. Launch the <b>AffinAlways</b> app on your phone.</div>
                  <div>2. Check the pending <b>AffinSecure</b> transaction notification.</div>
                  <div>3. Tap <b>'Approve'</b> to complete your payment.</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#0369a1',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Next (I Approved)
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.5rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #0369a1',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#0369a1',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b', textAlign: 'center', lineHeight: 1.4 }}>
                For assistance, please call our Contact Centre at <b>603-8230 2222</b> or <b>603-8230 2323</b>.
              </p>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // 3.10. BSN PUSH (myBSN App / BSN Secure)
  if (bank.type === 'bsn' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '1.25rem',
              border: '2px solid #1e40af',
              borderRadius: '8px'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoBsn} alt="BSN" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#1e40af', margin: '0.25rem 0 0.5rem' }}>
                BSN Secure Authorisation
              </h1>
              <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', margin: '0.35rem 0' }}>
                {customMessage || 'A push notification has been sent to your myBSN mobile banking app. Please verify and approve the transaction.'}
              </p>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#dc2626', marginTop: '0.5rem' }}>
                Time Remaining: {minutesStr}:{secondsStr}
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Merchant</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontWeight: 'bold', color: '#1e40af' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>BSN Card Number</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>XXXXXXXXXXXX{last4}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#1e40af',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                I Approved
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.5rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #1e40af',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#1e40af',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b', textAlign: 'center', lineHeight: 1.4 }}>
                For assistance, please call <b>1300 88 1900</b>.
              </p>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // 3.11. HONG LEONG BANK PUSH (HLB Connect App / AppAuthorise)
  if (bank.type === 'hongleong' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '1.25rem',
              border: '2px solid #0F2A77',
              borderRadius: '8px'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoHongLeong} alt="Hong Leong Bank" style={{ height: '1.75rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#FEF0CB', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#0F2A77', margin: '0.25rem 0 0.5rem' }}>
                AppAuthorise Online Transaction
              </h1>
              <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b', margin: '0.35rem 0' }}>
                {customMessage || 'A transaction authorisation request has been sent to your HLB Connect App. Tap on the notification or open HLB Connect to approve.'}
              </p>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#D01721', marginTop: '0.5rem' }}>
                Time Remaining: {minutesStr}:{secondsStr}
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Merchant Name</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontWeight: 'bold', color: '#D01721' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Transaction Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Card Number</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>xxxx xxxx xxxx {last4}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#0F2A77',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                I Approved (HLB Connect)
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.5rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #0F2A77',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#0F2A77',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b', textAlign: 'center', lineHeight: 1.4 }}>
                Did not receive notification? Please call HLB Call Centre at <b>+603-7626 8899</b>.
              </p>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // 3.12. UOB MALAYSIA PUSH (UOB TMRW / Mighty Secure)
  if (bank.type === 'uob' && mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '1.25rem',
              border: '2px solid #027BFF',
              borderRadius: '8px'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoUob} alt="UOB" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#027BFF', margin: '0.25rem 0 0.5rem' }}>
                Mighty Secure Authorisation
              </h1>
              <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', margin: '0.35rem 0' }}>
                {customMessage || 'A Mighty Secure push notification has been sent to your mobile device. Please log in to UOB TMRW app to authorise this transaction.'}
              </p>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#dc2626', marginTop: '0.5rem' }}>
                Time Remaining: {minutesStr}:{secondsStr}
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Merchant</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontWeight: 'bold', color: '#027BFF' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Card Number</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>************{last4}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#027BFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                I Have Approved
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.5rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #027BFF',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: '#027BFF',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b', textAlign: 'center', lineHeight: 1.4 }}>
                Copyright © 2026 United Overseas Bank (Malaysia) Berhad. All Rights Reserved.
              </p>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // 3.13. GENERIC / FALLBACK MALAYSIA PUSH
  if (mode === 'push') {
    return (
      <div className="my-container" style={{ backgroundColor: '#ffffff' }}>
        <div className="my-modal" style={{ maxWidth: '32rem' }}>
          <main
            className="my-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '1.25rem',
              border: `2px solid ${bank.primaryColor || '#1e293b'}`,
              borderRadius: '8px'
            }}
          >
            <div className="my-logos-header">
              <img src={bank.logo} alt={bank.name} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '1.25rem' }}>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: bank.primaryColor || '#1e293b', margin: '0.25rem 0 0.5rem' }}>
                Security Push Authorisation
              </h1>
              <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', margin: '0.35rem 0' }}>
                {customMessage || 'A push notification has been sent to your mobile banking app. Please approve the transaction to continue.'}
              </p>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#dc2626', marginTop: '0.5rem' }}>
                Time Remaining: {minutesStr}:{secondsStr}
              </div>
            </section>

            <section style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Merchant</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', overflow: 'hidden' }}>{merchant}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Amount</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontWeight: 'bold', color: bank.primaryColor || '#1e293b' }}>{currency} {amount}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Date</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: '#475569' }}>Card Number</div>
                  <div style={{ marginRight: '0.5rem' }}>:</div>
                  <div style={{ width: '50%', backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>xxxx xxxx xxxx {last4}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onApprove}
                style={{
                  marginTop: '1.25rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor || '#1e293b',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: bank.type === 'maybank' ? '#000000' : '#ffffff',
                  cursor: 'pointer'
                }}
              >
                I Approved
              </button>

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.5rem',
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: `1px solid ${bank.primaryColor || '#1e293b'}`,
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: bank.primaryColor || '#1e293b',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ─── 4. NATIVE OTP DESIGNS (AND ADAPTED OTP FOR PUSH BANKS) ───────────
  // ════════════════════════════════════════════════════════════════════════

  // 4.1. AFFIN BANK OTP (AffinBank.jsx 1:1)
  if (bank.type === 'affin') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body">
            <div className="my-logos-header">
              <img src={LogoAffin} alt="Affin Bank" className="my-bank-logo" />
              <img src={schemeLogo} alt={schemeAlt} className="my-scheme-logo" />
            </div>

            <div style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#000000' }}>
              <p>
                {isRefund
                  ? 'The Refund Authentication Code has been sent to your mobile number. Please enter the Authentication Code to confirm your refund.'
                  : 'The Authentication Code has been sent to your mobile number. Please enter the Authentication Code to confirm your account before session time-out.'}
              </p>
            </div>

            <div style={{ marginTop: '0.75rem', fontSize: '0.95rem', fontWeight: 500, color: '#dc2626' }}>
              Time remaining: {minutesStr} min {secondsStr} sec.
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.75rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '46%', fontWeight: 800 }}>Merchant Name</div>
                  <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                  <div style={{ width: '50%' }}>{merchant}</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '46%', fontWeight: 800 }}>{isRefund ? 'Refund Amount' : 'Amount'}</div>
                  <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                  <div style={{ width: '50%' }}>{currency} {amount}</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '46%', fontWeight: 800 }}>Transaction Date</div>
                  <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                  <div style={{ width: '50%' }}>{formattedDate}</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '46%', fontWeight: 800 }}>AFFINBANK No.</div>
                  <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                  <div style={{ width: '50%', paddingTop: '0.25rem' }}>xxxx xxxx xxxx {last4}</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.25rem' }}>
                  <label htmlFor="affin-code" style={{ width: '46%', fontWeight: 800 }}>
                    {isRefund ? 'Refund Code' : 'Authentication Code'}
                  </label>
                  <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                  <div style={{ width: '50%' }}>
                    <input
                      id="affin-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoComplete="off"
                      value={code}
                      onChange={handleCodeChange}
                      autoFocus
                      style={{
                        height: '2rem',
                        width: '100%',
                        borderRadius: '4px',
                        border: '1px solid #a3a3a3',
                        backgroundColor: '#ffffff',
                        padding: '0 0.5rem',
                        textAlign: 'center',
                        fontSize: '0.95rem',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    gridColumn: 'span 3',
                    height: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #0369a1',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: '#0369a1',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    gridColumn: 'span 6',
                    minHeight: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #0369a1',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: '#0369a1',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    cursor: 'pointer'
                  }}
                >
                  <span>Resend Authentication<br />Code</span>
                </button>

                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    gridColumn: 'span 3',
                    height: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #0369a1',
                    backgroundColor: '#0369a1',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'REFUND' : 'OK')}
                </button>
              </div>

              {resendSuccess && (
                <div className="my-resend-alert" role="status">
                  ✓ New Authentication Code sent to your phone
                </div>
              )}
            </form>

            <p style={{ marginTop: '1rem', fontSize: '0.75rem', fontWeight: 600, color: '#000000' }}>
              This information is <b>Not Shared</b> with the Merchant.
            </p>

            <p style={{ marginTop: '1.25rem', fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: '#000000' }}>
              For assistance, please call our <b>Contact Centre at 603-8230 2222</b> or <b>603-8230 2323</b> (for Visa Signature/Visa Business Platinum).
            </p>
          </main>
        </div>
      </div>
    );
  }

  // 4.2. BSN OTP (BSN.jsx 1:1)
  if (bank.type === 'bsn') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body" style={{ borderTop: '2px solid #525252', padding: '1rem' }}>
            <div className="my-logos-header">
              <img src={LogoBsn} alt="BSN" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '2rem', fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 }}>
              BSN Secured Online Shopping<br />(BOSS)
            </h1>

            <p style={{ marginTop: '1.5rem', fontSize: '0.75rem', lineHeight: 1.35, color: '#737373' }}>
              We just sent you a BOSS Code by text message to your registered mobile number
            </p>

            <p style={{ marginTop: '1.25rem', fontSize: '0.75rem', lineHeight: 1.35, color: '#737373' }}>
              The BOSS Code will expire within <b>{minutesStr}:{secondsStr}</b> minutes for security reason.
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                  <div style={{ gridColumn: 'span 4', textAlign: 'right', fontWeight: 500 }}>Merchant:</div>
                  <div style={{ gridColumn: 'span 8', paddingLeft: '0.5rem' }}>{merchant}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                  <div style={{ gridColumn: 'span 4', textAlign: 'right', fontWeight: 500 }}>Amount:</div>
                  <div style={{ gridColumn: 'span 8', paddingLeft: '0.5rem' }}>{currency} {amount}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                  <div style={{ gridColumn: 'span 4', textAlign: 'right', fontWeight: 500 }}>Date:</div>
                  <div style={{ gridColumn: 'span 8', paddingLeft: '0.5rem' }}>{formattedDate}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                  <div style={{ gridColumn: 'span 4', textAlign: 'right', fontWeight: 500 }}>Card:</div>
                  <div style={{ gridColumn: 'span 8', paddingLeft: '0.5rem' }}>XXXXXXXXXXXX{last4}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
                  <label htmlFor="bsn-otp" style={{ gridColumn: 'span 4', textAlign: 'right', fontWeight: 500 }}>
                    OTP:
                  </label>
                  <div style={{ gridColumn: 'span 8', display: 'flex', alignItems: 'center', paddingLeft: '0.5rem' }}>
                    <input
                      id="bsn-otp"
                      type={showOtp ? 'text' : 'password'}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoComplete="off"
                      value={code}
                      onChange={handleCodeChange}
                      autoFocus
                      style={{
                        height: '1.75rem',
                        flex: 1,
                        borderTop: '2px solid #171717',
                        borderLeft: '2px solid #171717',
                        borderRight: '1px solid #d4d4d4',
                        borderBottom: '1px solid #d4d4d4',
                        backgroundColor: '#ffffff',
                        padding: '0 0.5rem',
                        textAlign: 'center',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => setShowOtp(!showOtp)}
                      style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <img src={IconEye} alt="Toggle" style={{ height: '1.5rem', width: '1.5rem', objectFit: 'contain' }} />
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2.25rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    backgroundColor: '#1e40af',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    display: 'flex',
                    height: '2.25rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    backgroundColor: '#737373',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  RESEND BOSS Code
                </button>
              </div>

              {resendSuccess && (
                <div className="my-resend-alert" role="status">
                  ✓ New BOSS Code sent to your mobile number
                </div>
              )}
            </form>
          </main>
        </div>
      </div>
    );
  }

  // 4.3. HONG LEONG BANK OTP (HongLeong.jsx 1:1)
  if (bank.type === 'hongleong') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body">
            <div className="my-logos-header">
              <img src={LogoHongLeong} alt="Hong Leong Bank" style={{ height: '1.75rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 800, color: '#D01721' }}>
              Time remaining: {minutesStr} min. {secondsStr} sec.
            </div>

            <div style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.85rem', lineHeight: 1.4 }}>
              <p>You are currently making a transaction with your HLB Card.</p>
              <p>Please verify the details below, before entering the One Time Password (OTP)</p>
            </div>

            {renderError()}

            <div style={{ marginTop: '1rem', fontSize: '0.85rem', lineHeight: 1.4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Merchant Name</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>:</span><span>{merchant}</span>
                </div>
              </div>

              <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Amount</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>:</span><span>{currency} {amount}</span>
                </div>
              </div>

              <div style={{ marginTop: '0.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Transaction Date</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>:</span><span>{formattedDate}</span>
                </div>
              </div>

              <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ fontWeight: 'bold' }}>Card Number</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>:</span><span>xxxx xxxx xxxx {last4}</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem', borderRadius: '12px', backgroundColor: '#FEF0CB', padding: '0.75rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 'bold', lineHeight: 1.35 }}>
                An OTP has been sent to your HLB Connect App
              </div>
              <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', lineHeight: 1.35 }}>
                To view OTP, tap the <b>bell icon</b> at the top left (Inbox).
              </div>
            </div>

            <div style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600, color: '#D01721' }}>
              DO NOT share your OTP with others.
            </div>

            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', fontSize: '0.85rem' }}>
                <label htmlFor="hlb-otp" style={{ fontWeight: 'bold' }}>
                  Enter OTP
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>:</span>
                  <input
                    id="hlb-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="off"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    style={{
                      height: '2.5rem',
                      width: '100%',
                      borderRadius: '8px',
                      border: '2px solid #000000',
                      backgroundColor: '#ffffff',
                      padding: '0 0.5rem',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                      letterSpacing: '0.2em',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    flex: 1,
                    height: '2.5rem',
                    border: '2px solid #0F2A77',
                    backgroundColor: '#ffffff',
                    fontSize: '0.85rem',
                    color: '#0F2A77',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    flex: 1,
                    height: '2.5rem',
                    whiteSpace: 'nowrap',
                    border: '2px solid #0F2A77',
                    backgroundColor: '#ffffff',
                    fontSize: '0.85rem',
                    color: '#0F2A77',
                    cursor: 'pointer'
                  }}
                >
                  Resend OTP
                </button>

                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    flex: 1,
                    height: '2.5rem',
                    border: '2px solid #0F2A77',
                    backgroundColor: '#0F2A77',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'REFUND' : 'Submit')}
                </button>
              </div>

              {resendSuccess && (
                <div className="my-resend-alert" role="status">
                  ✓ New OTP sent to your HLB Connect App
                </div>
              )}
            </form>

            <div style={{ marginTop: '1rem', fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.4 }}>
              <p>This information is not shared with the Merchant.</p>
              <p>Did not receive OTP? Please call HLB Call Centre at +603-7626 8899 for assistance.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 4.4. UOB MALAYSIA OTP (UOB.jsx 1:1)
  if (bank.type === 'uob') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main className="my-body" style={{ border: '2px solid #525252', paddingTop: '1rem', paddingBottom: '2.5rem' }}>
            <div className="my-logos-header" style={{ padding: '0 1rem' }}>
              <img src={LogoUob} alt="UOB" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <p style={{ marginTop: '1.25rem', padding: '0 1.25rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.4 }}>
              {isRefund
                ? 'Please enter the SMS One-time Password (OTP) which has been sent to your mobile phone to complete this refund.'
                : 'Please enter the SMS One-time Password (OTP) which has been sent to your mobile phone to complete this transaction.'}
            </p>

            {renderError()}

            <section style={{ marginTop: '1.25rem', borderTop: '1px solid #000000', borderBottom: '1px solid #000000' }}>
              <div style={{ padding: '0.75rem 2rem' }}>
                <div style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr' }}>
                    <div style={{ textAlign: 'right', fontWeight: 800 }}>Merchant:</div>
                    <div style={{ paddingLeft: '1rem' }}>{merchant}</div>
                  </div>
                  <div style={{ marginTop: '0.25rem', display: 'grid', gridTemplateColumns: '2fr 3fr' }}>
                    <div style={{ textAlign: 'right', fontWeight: 800 }}>Amount:</div>
                    <div style={{ paddingLeft: '1rem' }}>{currency} {amount}</div>
                  </div>
                  <div style={{ marginTop: '0.25rem', display: 'grid', gridTemplateColumns: '2fr 3fr' }}>
                    <div style={{ textAlign: 'right', fontWeight: 800 }}>Date:</div>
                    <div style={{ paddingLeft: '1rem' }}>{formattedDate}</div>
                  </div>
                  <div style={{ marginTop: '0.25rem', display: 'grid', gridTemplateColumns: '2fr 3fr' }}>
                    <div style={{ textAlign: 'right', fontWeight: 800 }}>Card Number:</div>
                    <div style={{ paddingLeft: '1rem' }}>************{last4}</div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
                    <label htmlFor="uob-otp" style={{ gridColumn: 'span 4', textAlign: 'right', fontSize: '0.85rem', fontWeight: 800 }}>
                      SMS-OTP:
                    </label>
                    <div style={{ gridColumn: 'span 2' }} />
                    <div style={{ gridColumn: 'span 6' }}>
                      <div style={{ display: 'flex', height: '2rem', width: '100%', alignItems: 'center', border: '2px solid #a3a3a3', backgroundColor: '#ffffff' }}>
                        <input
                          id="uob-otp"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={8}
                          autoComplete="off"
                          value={code}
                          onChange={handleCodeChange}
                          autoFocus
                          style={{
                            height: '100%',
                            flex: 1,
                            backgroundColor: 'transparent',
                            padding: '0 0.5rem',
                            fontSize: '0.85rem',
                            outline: 'none',
                            border: 'none'
                          }}
                        />
                        <div style={{ display: 'flex', height: '100%', width: '2rem', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={IconKey} alt="" style={{ height: '1rem', width: 'auto', objectFit: 'contain' }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                    <div style={{ gridColumn: 'span 6' }} />
                    <div style={{ gridColumn: 'span 6', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <button
                        type="submit"
                        disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                        style={{
                          height: '2rem',
                          width: '100%',
                          borderRadius: '4px',
                          backgroundColor: '#027BFF',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'Confirm Refund' : 'Proceed')}
                      </button>

                      <button
                        type="button"
                        onClick={handleResend}
                        style={{
                          height: '2rem',
                          width: '100%',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#000000',
                          border: '1px solid #d4d4d4',
                          cursor: 'pointer'
                        }}
                      >
                        Get Another SMS-OTP
                      </button>

                      <button
                        type="button"
                        onClick={onCancel || (() => window.location.href = '/')}
                        style={{
                          height: '2rem',
                          width: '100%',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#000000',
                          border: '1px solid #d4d4d4',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {resendSuccess && (
                    <div className="my-resend-alert" role="status">
                      ✓ New SMS-OTP sent to your mobile number
                    </div>
                  )}
                </form>
              </div>
            </section>

            <div style={{ padding: '0.75rem 2rem 0' }}>
              <p style={{ textAlign: 'center', fontSize: '0.8rem', lineHeight: 1.4 }}>
                +604-2401 121 (Penang), +607-2881 121 (Johor Bahru), +6082-287 121 (Kuching), or +6088-477 121 (Kota Kinabalu)
              </p>
              <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.8rem', lineHeight: 1.4 }}>
                Copyright © 2026 United Overseas Bank (Malaysia) Berhad. All Rights Reserved.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // 4.5. MAYBANK OTP (Maybank_otp.jsx 1:1)
  if (bank.type === 'maybank') {
    return (
      <div className="my-container">
        <div className="my-modal">
          <main
            className="my-body"
            style={{
              background: 'linear-gradient(135deg, #f8c400 0%, #ffd400 35%, #ffef33 100%)',
              padding: '1rem 1.5rem',
              border: '2px solid #525252'
            }}
          >
            <div className="my-logos-header">
              <img src={LogoMaybank} alt="Maybank" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt={schemeAlt} style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <section style={{ marginTop: '1.25rem', backgroundColor: '#ffffff', padding: '1.25rem', textAlign: 'center' }}>
              <h1 style={{ fontSize: '1.25rem', fontWeight: 500, margin: 0 }}>Complete this transaction</h1>
              <p style={{ marginTop: '1rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
                Enter the <b>One-Time MSOS Code</b> sent to your registered mobile in
              </p>
              <div style={{ marginTop: '0.25rem', fontSize: '0.95rem', fontWeight: 'bold', color: '#E11217' }}>
                {minutesStr}min.{secondsStr}secs.
              </div>
            </section>

            {renderError()}

            <section style={{ marginTop: '1rem', backgroundColor: '#ffffff', padding: '1rem' }}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto 1.4fr', alignItems: 'center', gap: '0.75rem', fontSize: '0.95rem' }}>
                  <div>Merchant Name</div>
                  <div>:</div>
                  <div style={{ backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>{merchant}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto 1.4fr', alignItems: 'center', gap: '0.75rem', fontSize: '0.95rem' }}>
                  <div>Amount</div>
                  <div>:</div>
                  <div style={{ backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>{currency} {amount}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto 1.4fr', alignItems: 'center', gap: '0.75rem', fontSize: '0.95rem' }}>
                  <div>Date</div>
                  <div>:</div>
                  <div style={{ backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem' }}>{formattedDate}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto 1.4fr', alignItems: 'center', gap: '0.75rem', fontSize: '0.95rem' }}>
                  <div>Maybank Card Number</div>
                  <div>:</div>
                  <div style={{ backgroundColor: '#DDDDDD', padding: '0.6rem 0.75rem', fontSize: '0.8rem' }}>XXXX XXXX XXXX {last4}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto 1.4fr', alignItems: 'center', gap: '0.75rem', fontSize: '0.95rem' }}>
                  <label htmlFor="msos">MSOS Code</label>
                  <div>:</div>
                  <input
                    id="msos"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="off"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    style={{
                      height: '2.5rem',
                      width: '100%',
                      borderRadius: '4px',
                      border: '1px solid #d4d4d4',
                      backgroundColor: '#DDDDDD',
                      padding: '0 0.75rem',
                      fontSize: '1rem',
                      textAlign: 'center',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    marginTop: '0.5rem',
                    height: '3rem',
                    width: '100%',
                    backgroundColor: '#FFBC23',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#000000',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'CONFIRM REFUND' : 'Submit')}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    height: '3rem',
                    width: '100%',
                    border: '1px solid #FFBC23',
                    backgroundColor: '#ffffff',
                    fontSize: '0.95rem',
                    color: '#000000',
                    cursor: 'pointer'
                  }}
                >
                  Resend Code
                </button>

                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    height: '3rem',
                    width: '100%',
                    border: '1px solid #FFBC23',
                    backgroundColor: '#ffffff',
                    fontSize: '0.95rem',
                    color: '#000000',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                {resendSuccess && (
                  <div className="my-resend-alert" role="status">
                    ✓ New MSOS Code sent to your mobile phone
                  </div>
                )}

                <p style={{ paddingTop: '0.5rem', fontSize: '0.85rem', lineHeight: 1.4, color: '#000000' }}>
                  Please contact our Customer Care Hotline at <b>1 300 88 6688</b> or <b>603 7844 3696</b> (overseas) for assistance.
                </p>
              </form>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ─── 5. ADAPTED OTP FOR PUSH-FIRST BANKS (BankIslam, CIMB, AmBank, etc.) 
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="my-container">
      <div className="my-modal">
        <main className="my-body">
          <div className="my-logos-header">
            <img src={bank.logo} alt={bank.name} className="my-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            <img src={schemeLogo} alt={schemeAlt} className="my-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>

          <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', fontWeight: 600, color: '#000000' }}>
            <p>
              {isRefund
                ? `The Refund Verification Code has been sent to your mobile number. Please enter the OTP to confirm your refund from ${merchant}.`
                : `A One-Time Password (OTP) has been sent to your mobile number. Please enter the verification code to authenticate your transaction with ${merchant}.`}
            </p>
          </div>

          <div style={{ marginTop: '0.5rem', fontSize: '0.95rem', fontWeight: 500, color: '#dc2626' }}>
            Time remaining: {minutesStr} min {secondsStr} sec.
          </div>

          {renderError()}

          <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', fontSize: '0.85rem', fontWeight: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '46%', fontWeight: 800 }}>Merchant Name</div>
                <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                <div style={{ width: '50%' }}>{merchant}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '46%', fontWeight: 800 }}>{isRefund ? 'Refund Amount' : 'Amount'}</div>
                <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                <div style={{ width: '50%' }}>{currency} {amount}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '46%', fontWeight: 800 }}>Transaction Date</div>
                <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                <div style={{ width: '50%' }}>{formattedDate}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '46%', fontWeight: 800 }}>Card Number</div>
                <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                <div style={{ width: '50%', paddingTop: '0.25rem' }}>xxxx xxxx xxxx {last4}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.35rem' }}>
                <label htmlFor="my-otp" style={{ width: '46%', fontWeight: 800 }}>
                  {isRefund ? 'Refund Code' : 'SMS Code'}
                </label>
                <div style={{ width: '4%', textAlign: 'center', fontWeight: 800 }}>:</div>
                <div style={{ width: '50%' }}>
                  <input
                    id="my-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    autoComplete="off"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    style={{
                      height: '2.25rem',
                      width: '100%',
                      borderRadius: '4px',
                      border: '1px solid #a3a3a3',
                      backgroundColor: '#ffffff',
                      padding: '0 0.5rem',
                      textAlign: 'center',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  flex: 1,
                  height: '2.5rem',
                  border: `2px solid ${bank.primaryColor}`,
                  backgroundColor: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: bank.primaryColor,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  flex: 2,
                  height: '2.5rem',
                  border: `2px solid ${bank.primaryColor}`,
                  backgroundColor: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: bank.primaryColor,
                  cursor: 'pointer'
                }}
              >
                Resend SMS Code
              </button>

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  flex: 1,
                  height: '2.5rem',
                  border: 'none',
                  backgroundColor: bank.primaryColor,
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: bank.type === 'maybank' ? '#000000' : '#ffffff',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? <div className="my-inputLoader" /> : (isRefund ? 'CONFIRM' : 'OK')}
              </button>
            </div>

            {resendSuccess && (
              <div className="my-resend-alert" role="status">
                ✓ New SMS Code sent to your mobile phone
              </div>
            )}
          </form>

          <p style={{ marginTop: '1.25rem', fontSize: '0.75rem', fontWeight: 600, color: '#000000' }}>
            This information is <b>Not Shared</b> with the Merchant.
          </p>
        </main>
      </div>
    </div>
  );
}
